"use client"

import { useState, useEffect } from "react"
import ModernHeader from "./modern-header"
import ModernSidebar from "./admin_sidebar"
import { getTickets, createTicket } from '../utils/api';
import { toast, Toaster } from 'react-hot-toast';
import 'react-toastify/dist/ReactToastify.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ClipLoader } from "react-spinners";
import axios from "axios";
import { PhoneCall, X, Phone } from "lucide-react";
import { isGuest } from "../utils/isGuest";
import { useNavigate } from "react-router-dom";

// Expert Modal Component
const ExpertModal = ({ isOpen, onClose, sectorContacts, startupProfile, handleCallExpert }) => {
  if (!isOpen) return null;

  const getSectorPhoneNumber = () => {
    if (!startupProfile || !startupProfile.sector || !sectorContacts || Object.keys(sectorContacts).length === 0) return null;

    const startupSector = startupProfile.sector.toLowerCase().trim();
    
    // Check if any sector key contains the startup's sector (or vice versa)
    const sectorKey = Object.keys(sectorContacts).find(
      key => key.toLowerCase().includes(startupSector) || startupSector.includes(key.toLowerCase())
    );

    if (sectorKey && sectorContacts[sectorKey] && sectorContacts[sectorKey].length > 0) {
      return sectorContacts[sectorKey][0];
    }

    // If no match found, try to get the "Reports" contact specifically
    if (sectorContacts["Reports"] && sectorContacts["Reports"].length > 0) {
      return sectorContacts["Reports"][0];
    }

    return null;
  };

  const getAnyContactNumber = () => {
    if (!sectorContacts || Object.keys(sectorContacts).length === 0) return null;

    // First try to get the Reports contact
    if (sectorContacts["Reports"] && sectorContacts["Reports"].length > 0) {
      return sectorContacts["Reports"][0];
    }

    // If no Reports contact, get the first available contact
    const firstSector = Object.keys(sectorContacts)[0];
    if (sectorContacts[firstSector] && sectorContacts[firstSector].length > 0) {
      return sectorContacts[firstSector][0];
    }

    return null;
  };

  const hasContacts = () => {
    return sectorContacts && Object.keys(sectorContacts).length > 0 &&
      Object.values(sectorContacts).some(numbers => numbers && numbers.length > 0);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative transform animate-in zoom-in-95 duration-300 expert-modal">
        <div className="absolute top-4 right-4">
          <button
            onClick={onClose}
            className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="text-center mb-6">
          <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <PhoneCall className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Call Our Support Expert</h2>
          <p className="text-gray-600">
            Get personalized assistance with your reports and support ticket issues.
          </p>
        </div>

        {hasContacts() ? (
          <>
            {startupProfile && startupProfile.sector && getSectorPhoneNumber() ? (
              <div className="bg-blue-50 rounded-xl p-5 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Sector-Specific Support</h3>
                <p className="text-gray-700 mb-3">
                  Your startup is in the <span className="font-semibold">{startupProfile.sector}</span> sector.
                  Contact our dedicated support expert for specialized assistance.
                </p>
                <div className="flex items-center justify-center gap-2 text-lg font-semibold text-blue-700">
                  <Phone className="w-5 h-5" />
                  <span>{getSectorPhoneNumber()}</span>
                </div>
              </div>
            ) : (
              <div className="bg-purple-50 rounded-xl p-5 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">General Support</h3>
                <p className="text-gray-700 mb-3">
                  Contact our support experts for assistance with your reports and ticket issues.
                </p>
                <div className="flex items-center justify-center gap-2 text-lg font-semibold text-purple-700">
                  <Phone className="w-5 h-5" />
                  <span>{getAnyContactNumber()}</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-gray-50 rounded-xl p-5 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Support Unavailable</h3>
            <p className="text-gray-700 mb-3">
              Our support team contact information is currently being updated.
            </p>
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Please check back later or email us at support@tactos.in
              </p>
            </div>
          </div>
        )}

        {hasContacts() && (
          <button
            onClick={() => handleCallExpert(getSectorPhoneNumber() || getAnyContactNumber())}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-3"
          >
            <PhoneCall className="w-5 h-5" />
            Call Now
          </button>
        )}
      </div>
    </div>
  );
};

export default function Report() {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [selectedTicket, setSelectedTicket] = useState(null)
    const [subject, setSubject] = useState("")
    const [description, setDescription] = useState("")
    const [priority, setPriority] = useState("Low")
    const toggleSidebar = () => setSidebarOpen((prev) => !prev);
    const [isMobile, setIsMobile] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [email, setEmail] = useState("");
    const [fullName, setFullName] = useState("");
    const [showExpertModal, setShowExpertModal] = useState(false);
    const [sectorContacts, setSectorContacts] = useState({});
    const queryClient = useQueryClient();
    const navigate=useNavigate()

    // Fetch sector contacts
    const getSectorContacts = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sector-contacts`);
        return response.data;
      } catch (error) {
        console.error('Error fetching sector contacts:', error);
        throw error;
      }
    };

    useEffect(() => {
      const fetchSectorContacts = async () => {
        try {
          const contacts = await getSectorContacts();
          setSectorContacts(contacts);
        } catch (error) {
          console.error('Error fetching sector contacts:', error);
        }
      };

      fetchSectorContacts();
    }, []);

    // Fetch tickets with React Query
    const { data: tickets, isLoading, isError } = useQuery({
        queryKey: ['tickets'],
        queryFn: async () => {
            try {
                const response = await getTickets();
                // Log the response to debug
                console.log('API Response:', response);

                if (!response) {
                    throw new Error('No data received');
                }

                // Handle different response structures
                const data = Array.isArray(response) ? response :
                    response.data ? response.data :
                        response.tickets ? response.tickets : [];

                return data.map((ticket, index) => ({
                    ...ticket,
                    serialNo: index + 1,
                    status: formatStatus(ticket.status),
                    lastUpdated: new Date(ticket.updatedAt || ticket.createdAt).toLocaleString(),
                    formattedId: `TKT-${ticket._id?.slice(-6).toUpperCase() || 'XXXXXX'}`
                }));
            } catch (err) {
                console.error('Ticket fetch error:', err);
                throw err; // This will trigger isError
            }
        },
        onError: (err) => {
            console.error('Failed to load tickets', err);
            toast.error(err.message || 'Failed to load tickets');
        }
    });

    // Create ticket mutation
    const createTicketMutation = useMutation({
        mutationFn: createTicket,
        onSuccess: (newTicket) => {
            // Invalidate and refetch
            queryClient.invalidateQueries(['tickets']);
            toast.success(`Ticket created successfully!`);
            // Reset form
            setSubject("");
            setDescription("");
            setPriority("Low");
            setIsModalOpen(false);
        },
        onError: (err) => {
            console.error("Failed to submit ticket", err);
            toast.error(err.message || "Ticket creation failed");
        }
    });

    const formatStatus = (status) => {
        switch (status) {
            case 'open': return 'Open';
            case 'in_progress': return 'In Progress';
            case 'resolved': return 'Resolved';
            case 'closed': return 'Closed';
            default: return status;
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!subject || !description) {
            toast.warn("Please fill in all required fields.");
            return;
        }

        try {
            const user = localStorage.getItem("startupId");
            if (!user) {
                throw new Error("User not authenticated");
            }

            const payload = {
                user,
                subject,
                description,
                priority: priority.toLowerCase(),
            };

            await createTicketMutation.mutateAsync(payload);
        } catch (err) {
            console.error("Failed to submit ticket", err);
        }
    };

    const handleViewTicket = (ticket) => {
        setSelectedTicket(ticket);
        setViewModalOpen(true);
    }

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "Open":
                return "bg-green-500 text-white"
            case "In Progress":
                return "bg-blue-500 text-white"
            case "Resolved":
                return "bg-purple-500 text-white"
            case "Closed":
                return "bg-gray-500 text-white"
            default:
                return "bg-gray-200 text-gray-800"
        }
    }

    const getPriorityBadgeClass = (priority) => {
        switch (priority) {
            case "High":
                return "bg-red-500 text-white"
            case "Urgent":
                return "bg-purple-600 text-white"
            case "Medium":
                return "bg-orange-500 text-white"
            case "Low":
                return "bg-blue-400 text-white"
            default:
                return "bg-gray-200 text-gray-800"
        }
    }

    const handleCallExpert = (phoneNumber) => {
      const cleanedPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');
      window.location.href = `tel:${cleanedPhoneNumber}`;
    };

    // Close modals when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isModalOpen && e.target.classList.contains('bg-black/50')) {
                setIsModalOpen(false);
            }
            if (viewModalOpen && e.target.classList.contains('bg-black/50')) {
                setViewModalOpen(false);
            }
            if (showExpertModal && e.target.classList.contains('bg-black/60')) {
                setShowExpertModal(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isModalOpen, viewModalOpen, showExpertModal]);

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Sidebar */}
            <ModernSidebar
                isOpen={sidebarOpen}
                toggleSidebar={toggleSidebar}
                isMobile={isMobile}
                fullName={fullName}
                email={email}
            />

            {/* Main Content */}
            <div className={`flex flex-col flex-1 transition-all duration-300 ${isMobile ? "ml-0" : "ml-16 md:ml-20 lg:ml-20"}`}>
                <ModernHeader toggleSidebar={toggleSidebar} isMobile={isMobile} />

                {/* Page Content */}
                <main className="flex-1 overflow-y-auto p-6 bg-gray-50">
                    {/* Card */}
                    <div className="bg-white rounded-lg shadow-md w-full">
                        {/* CardHeader */}
                        <div className="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
                            <div className="space-y-1">
                                <h2 className="text-2xl font-bold">Support Tickets</h2>
                                <p className="text-sm text-gray-500">A comprehensive overview of all support tickets.</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowExpertModal(true)}
                                    className="group relative overflow-hidden bg-black hover:bg-gray-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 sm:gap-3 text-sm sm:text-base"
                                >
                                    <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                                    <PhoneCall className="w-4 h-4 sm:w-5 sm:h-5" />
                                    <span>Call to Expert</span>
                                </button>

                                <button
                                    onClick={() => {
                                        if(isGuest()){
                                            toast.error("Please login to raise a ticket")
                                            navigate("/login")
                                        }else{
                                            setIsModalOpen(true)
                                        }
                                    }}
                                    className="px-4 sm:px-6 py-2 sm:py-3 bg-black hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-xs sm:text-sm"
                                >
                                    Create New Ticket
                                </button>
                            </div>
                        </div>
                        {/* CardContent */}
                        <div className="p-6 pt-0">
                            <div className="overflow-x-auto">
                                {/* Table */}
                                <table className="w-full caption-bottom text-sm">
                                    {/* TableHeader */}
                                    <thead className="[&_tr]:border-b">
                                        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                                            <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 w-[80px]">
                                                S.No
                                            </th>
                                            <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0">
                                                Subject
                                            </th>
                                            <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0">
                                                Status
                                            </th>
                                            <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0">
                                                Priority
                                            </th>
                                            <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0">
                                                Last Updated
                                            </th>
                                            <th className="h-12 px-4 text-right align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 w-[50px]">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    {/* TableBody */}
                                    <tbody className="[&_tr:last-child]:border-0">
                                        {isLoading ? (
                                            // Skeleton loader while loading
                                            Array(5).fill(0).map((_, index) => (
                                                <tr key={index} className="border-b transition-colors hover:bg-muted/50">
                                                    <td className="p-4 align-middle font-medium"><Skeleton width={30} /></td>
                                                    <td className="p-4 align-middle"><Skeleton width={150} /></td>
                                                    <td className="p-4 align-middle"><Skeleton width={80} /></td>
                                                    <td className="p-4 align-middle"><Skeleton width={80} /></td>
                                                    <td className="p-4 align-middle text-right"><Skeleton width={120} /></td>
                                                    <td className="p-4 align-middle text-right"><Skeleton width={50} /></td>
                                                </tr>
                                            ))
                                        ) :isGuest()?(
                                            <tr>
                                                <td colSpan={6} className="p-4 text-center text-black">
                                                    Please Login to View Tickets
                                                </td>
                                            </tr>
                                        ) :isError ? (
                                            <tr>
                                                <td colSpan={6} className="p-4 text-center text-red-500">
                                                    Failed to load tickets
                                                </td>
                                            </tr>
                                        ) : tickets?.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="p-4 text-center text-gray-500">
                                                    No tickets found
                                                </td>
                                            </tr>
                                        ) : (
                                            tickets?.map((ticket) => (
                                                <tr
                                                    key={ticket._id}
                                                    className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                                                >
                                                    <td className="p-4 align-middle font-medium">{ticket.serialNo}</td>
                                                    <td className="p-4 align-middle">{ticket.subject}</td>
                                                    <td className="p-4 align-middle">
                                                        <span
                                                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(ticket.status)}`}
                                                        >
                                                            {ticket.status}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 align-middle">
                                                        <span
                                                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPriorityBadgeClass(ticket.priority)}`}
                                                        >
                                                            {ticket.priority}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 align-middle text-right">{ticket.lastUpdated}</td>
                                                    <td className="p-4 align-middle text-right">
                                                        <button
                                                            onClick={() => handleViewTicket(ticket)}
                                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-8 px-2 py-1 bg-gray-100 hover:bg-gray-200"
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Create Ticket Modal */}
                    {isModalOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-6">
                                <div className="flex flex-col space-y-1.5 text-center sm:text-left">
                                    <h3 className="text-lg font-semibold leading-none tracking-tight">Create New Support Ticket</h3>
                                    <p className="text-sm text-muted-foreground">Fill in the details below to submit a new support ticket.</p>
                                </div>
                                <form onSubmit={handleSubmit} className="grid gap-4 py-4">
                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <label htmlFor="subject" className="text-right text-sm font-medium leading-none">
                                            Subject
                                        </label>
                                        <input
                                            id="subject"
                                            value={subject}
                                            onChange={(e) => setSubject(e.target.value)}
                                            className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            required
                                        />
                                    </div>
                                    <div className="grid grid-cols-4 items-start gap-4">
                                        <label htmlFor="description" className="text-right pt-2 text-sm font-medium leading-none">
                                            Description
                                        </label>
                                        <textarea
                                            id="description"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            className="col-span-3 flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            required
                                        />
                                    </div>
                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <label htmlFor="priority" className="text-right text-sm font-medium leading-none">
                                            Priority
                                        </label>
                                        <select
                                            id="priority"
                                            value={priority}
                                            onChange={(e) => setPriority(e.target.value)}
                                            className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            required
                                        >
                                            <option value="Low">Low</option>
                                            <option value="Medium">Medium</option>
                                            <option value="High">High</option>
                                            <option value="Urgent">Urgent</option>
                                        </select>
                                    </div>
                                    <div className="flex justify-end gap-2 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            disabled={createTicketMutation.isLoading}
                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-100"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={createTicketMutation.isLoading}
                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 bg-black text-white hover:bg-gray-800"
                                        >
                                            {createTicketMutation.isLoading ? (
                                                <>
                                                    <ClipLoader size={18} />
                                                    Submitting...
                                                </>
                                            ) : (
                                                "Submit Ticket"
                                            )}
                                        </button>
                                    </div>
                                </form>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    disabled={createTicketMutation.isLoading}
                                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:visible:ring-offset-2 disabled:pointer-events-none"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="h-4 w-4"
                                    >
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                    </svg>
                                    <span className="sr-only">Close</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* View Ticket Modal */}
                    {viewModalOpen && selectedTicket && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl p-6">
                                <div className="flex flex-col space-y-1.5 text-center sm:text-left">
                                    <h3 className="text-lg font-semibold leading-none tracking-tight">
                                        Ticket Details: {selectedTicket.formattedId || `TKT-${selectedTicket._id.slice(-6).toUpperCase()}`}
                                    </h3>
                                </div>
                                <div className="grid gap-4 py-4">
                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <label className="text-right text-sm font-medium leading-none">
                                            Subject
                                        </label>
                                        <div className="col-span-3 p-2 bg-gray-50 rounded">
                                            {selectedTicket.subject}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-4 items-start gap-4">
                                        <label className="text-right pt-2 text-sm font-medium leading-none">
                                            Description
                                        </label>
                                        <div className="col-span-3 p-2 bg-gray-50 rounded min-h-[100px]">
                                            {selectedTicket.description}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <label className="text-right text-sm font-medium leading-none">
                                            Status
                                        </label>
                                        <div className="col-span-3">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(selectedTicket.status)}`}>
                                                {selectedTicket.status}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <label className="text-right text-sm font-medium leading-none">
                                            Priority
                                        </label>
                                        <div className="col-span-3">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPriorityBadgeClass(selectedTicket.priority)}`}>
                                                {selectedTicket.priority}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <label className="text-right text-sm font-medium leading-none">
                                            Created
                                        </label>
                                        <div className="col-span-3 p-2">
                                            {new Date(selectedTicket.createdAt).toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <label className="text-right text-sm font-medium leading-none">
                                            Last Updated
                                        </label>
                                        <div className="col-span-3 p-2">
                                            {selectedTicket.lastUpdated || new Date(selectedTicket.updatedAt).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end pt-4">
                                    <button
                                        onClick={() => setViewModalOpen(false)}
                                        className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 bg-black text-white hover:bg-gray-800"
                                    >
                                        Close
                                    </button>
                                </div>
                                <button
                                    onClick={() => setViewModalOpen(false)}
                                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="h-4 w-4"
                                    >
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                    </svg>
                                    <span className="sr-only">Close</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Expert Modal */}
                    <ExpertModal
                      isOpen={showExpertModal}
                      onClose={() => setShowExpertModal(false)}
                      sectorContacts={sectorContacts}
                      startupProfile={null} // You might want to fetch profile data here
                      handleCallExpert={handleCallExpert}
                    />
                </main>
            </div>
        </div>
    )
}